// @flow

import { toASCII } from 'punycode.js';
import { disbatch, createAction } from 'redux-act';

import { selectTaxesDisplaySetting } from '@gandi/authentication';
import { selectPricingInfos } from '@gandi/billing';
import type { FetchResponse } from '@gandi/resource/redux';

import { GET } from 'gandi.redux-fetcher';
import { logging } from 'gandi.redux-logger';

import type { GetState, Dispatch } from '../../../types';
import { select } from '../selectors/idn';

import { setAvailability, setBilling } from './results';

type ExtendedError = Error & {
  _error?: string,
  [key: string]: any,
  ...
};

const loggerNamespace = 'DomainCreate/modules/actions/idn';

export const setLanguages: {
  (...Array<any>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[idn] Set available languages');

export const fetchIDNAvailableLanguages =
  (): ((dispatch: Dispatch, getState: GetState) => Promise<void>) =>
  (dispatch: Dispatch, getState: GetState) => {
    const logger = logging(dispatch, loggerNamespace);

    if (select(getState()).length > 0) return Promise.resolve();

    return dispatch(
      GET('/idn/langs', {
        // $FlowFixMe[incompatible-exact]
        on200: (response: FetchResponse<200, any>) => {
          const langs = response.data;

          if (Object.keys(langs).length > 0) {
            dispatch(
              setLanguages(
                langs.map((lang: { [code: string]: string }) => {
                  const code = Object.keys(lang)[0];
                  return { value: code, text: lang[code] };
                }),
              ),
            );
          }

          return response;
        },
        not200: () => {
          logger.error('Unable to fetch IDN Available Languages');
        },
      }),
    );
  };

export const fetchIDNLookup =
  (
    fqdn: string,
    lang: string,
    requiresLang: boolean = false,
  ): ((dispatch: Dispatch, getState: GetState) => any) =>
  (dispatch: Dispatch, getState: GetState) => {
    const logger = logging(dispatch, loggerNamespace);
    const pricingInfos = selectPricingInfos(getState());
    const taxesIncluded = selectTaxesDisplaySetting(getState());

    const params = {
      ...pricingInfos,
      lang,
      search: toASCII(fqdn),
    };

    // $FlowFixMe[prop-missing]
    return dispatch(GET('/suggest/lookup', { params }))
      .catch(() => ({ data: undefined })) // Likely NetworkError, handle like a fetch error.
      .then(response => {
        let error: ?ExtendedError;

        const lookup = response.data;
        if (response.status !== 200 || !lookup) {
          // $FlowFixMe[class-object-subtyping]
          error = (new Error('Unable to fetch IDN lookup'): ExtendedError);
          error._error = 'fetch';
        }

        if (!error) {
          if (lookup.availability !== 'available') {
            // $FlowFixMe[class-object-subtyping]
            error = (new Error('IDN is not available'): ExtendedError);
            error._error = 'availability';
            const lookupAvailability = { fqdn, availability: 'lang_required' };
            // $FlowFixMe[not-an-array]
            dispatch(setAvailability(lookupAvailability));
          } else if (!Object.keys(lookup.prices).length) {
            // $FlowFixMe[class-object-subtyping]
            error = (new Error('No prices found for IDN'): ExtendedError);
            error._error = 'pricing';
          }
        }

        if (error) {
          error.fqdn = fqdn;
          error.lang = lang;
          error.response = response;
          error.lookup = lookup;

          throw error;
        }

        const lookupAvailability = {
          fqdn: toASCII(lookup.fqdn),
          premium: lookup.premium,
          availability: lookup.availability,
        };

        const lookupPrices = {
          fqdn: toASCII(lookup.fqdn),
          prices: lookup.prices,
          taxesIncluded,
        };

        // Always dispatch das/billing success actions if the lookup was successful,
        // whether the lang param is mandatory or optional.
        disbatch(
          dispatch,
          // $FlowFixMe[not-an-array]
          setAvailability(lookupAvailability),
          // $FlowFixMe[not-an-array]
          setBilling(lookupPrices),
        );

        return response;
      })
      .catch(error => {
        logger.error(
          {
            response: error.response,
            body: { fqdn, lang, requiresLang },
            error,
          },
          error.name,
        );

        throw error;
      });
  };
